/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable @typescript-eslint/naming-convention */
'use client';

import React, { useState, ChangeEvent, KeyboardEvent, useEffect } from 'react';
import CollectPanModal from '../../shared/Components/CollectPanModal';
import { screensMap } from '..';
import { USER_DETAILS_LABELS } from './constants';
import { USER_DETAILS_TYPE } from './types';
import EmiPlansLoader from '@/shared/Components/EmiPlansLoader';
import Button from '@/lib/Button';
import Chip from '@/lib/Chip';
import Icon from '@/lib/Icon/Icon';
import Input from '@/lib/Input';
import { useGlobalStore } from '@/store';
import { isValidPin } from '@/utils/helpers';
import { USER_DETAILS_TYPES, USER_KEYS } from '@/@types/globals';
import { getUserLoggedInStatus, trackHelper } from '@/analytics/utils';
import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';

const Details = (): JSX.Element => {
  const setView = useGlobalStore((state) => state.setActiveView);
  const { paymentDetails, user, xDataToken, setUser, userAccessToken } = useGlobalStore();
  const { amount } = paymentDetails.data ?? {};
  const paymentAmount = Number(amount);

  const userDetailsMap: USER_DETAILS_TYPE = {
    pan: 'pan',
    name: 'name',
    dob: 'dob',
  };

  const [pincode, setPincode] = useState('');
  const [isValidPinCode, setIsValidPinCode] = useState(true);
  const [showEditPan, setShowEditPan] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [showPanUpdateError, setShoPanUpdateError] = useState(false);

  const handlePinCodeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setIsValidPinCode(true);
    setPincode(value);
  };

  const handleBlur = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setIsValidPinCode(isValidPin(value));
  };

  const handleInputFocus = (): void => {
    // track Edit Pin code click
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      objectName: EVENT_NAMES.EDIT_PIN_CLICKED,
      screen: screensMap.DETAILS,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        pan_filled: Boolean(user.pan),
        pin_code_filled: Boolean(user.pin_code ?? pincode),
      },
    });
  };

  const showEmployerVerficationScreen = (): boolean => {
    return paymentAmount / 100 > 20000;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!isValidPin(pincode)) {
      setIsValidPinCode(false);
      return;
    }

    setUser({
      ...user,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      pin_code: pincode,
    });

    if (showEmployerVerficationScreen()) {
      setView('EMPLOYER_DETAILS');
      return;
    }

    setIsSavingDetails(true);
  };

  const handleEditPan = (): void => {
    setShowEditPan(true);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      // submit Pin code on enter press
      handleSubmit();
    }
  };

  const handleDismiss = (showError = false): void => {
    setShowEditPan(false);
    setShoPanUpdateError(showError);
  };

  useEffect(() => {
    // track details page render
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.DETAILS_PAGE_RENDERED,
      screen: screensMap.DETAILS,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        fc_customer_id: user.id,
        pan_filled: Boolean(user.pan),
        name_filled: Boolean(user.name),
        dob_filled: Boolean(user.dob),
      },
    });
  }, []);

  return (
    <>
      <div className="relative h-full d:flex d:items-center d:justify-center">
        <div className="p-6 pt-3 grow overflow-scroll d:grow-0 d:w-70 checkoutV2:p-0 checkoutV2:w-1/2">
          <h3 className="font-tasa font-semibold text-2xl text-on-grey-normal">
            Verify PAN and personal details{' '}
          </h3>
          <div
            className={`p-4 my-6 border-outline-grey-subtle border rounded-xl ${
              showPanUpdateError ? 'mb-0' : 'mb-6'
            }`}
          >
            {Object.keys(userDetailsMap).map((item, key) => {
              return (
                <React.Fragment key={key}>
                  {item !== 'pincode' ? (
                    <div className="flex justify-between mb-1 last:mb-0">
                      <p className="text-on-grey-subtle text-sm uppercase">
                        {USER_DETAILS_LABELS[item as USER_DETAILS_TYPES]}
                      </p>
                      <p className="text-sm text-on-grey-normal flex">
                        {user[item as keyof typeof user] || '-'}
                        {item === 'pan' ? (
                          <button data-testid="edit-pan" onClick={handleEditPan} className="ml-1">
                            <Icon name="edit" />{' '}
                          </button>
                        ) : (
                          ''
                        )}
                      </p>
                    </div>
                  ) : null}
                </React.Fragment>
              );
            })}
          </div>

          {showPanUpdateError ? (
            <Chip className="[&&]:bg-notice-faded [&&]:bg-opacity-9 [&&]:p-2.5 [&&]:px-2 border-0 mt-2 mb-6 [&&]:rounded-lg">
              <Icon name="warning" className="w-8" fill="fill-notice-intense" />
              {/* <Image className='w-20 h-20 fill-white' src={''} /> */}
              <p className="text-sm text-notice-intense">
                You’ve reached maximum edits allowed for PAN
              </p>
            </Chip>
          ) : null}

          <Input
            value={pincode}
            onChange={handlePinCodeChange}
            onBlur={handleBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder="Pincode"
            error={!isValidPinCode ? 'Pincode is not valid' : ''}
          />
          {isValidPinCode}
          <Chip className="[&&]:bg-information-default [&&]:bg-opacity-18 [&&]:p-1.5 [&&]:px-1.5 border-0 mt-1.5 [&&]:rounded-lg">
            <Icon name="error" className="mr-1.5" fill="fill-information-intense" />
            {/* <Image className='w-20 h-20 fill-white' src={''} /> */}
            <p className="text-sm text-information-intense">
              As per your current residential address
            </p>
          </Chip>
          <div className="shrink-0 left-0 align-bottom p-6 pt-2 w-full shadow-top bg-white-normal absolute bottom-0 d:shrink-unset d:shadow-none d:p-0 d:relative d:mt-3">
            <Button disabled={showPanUpdateError} onClick={handleSubmit} className="w-full">
              {showEmployerVerficationScreen()
                ? 'Proceed to Income Verification'
                : 'Explore EMI Offers'}
            </Button>
          </div>
        </div>
      </div>
      {showEditPan ? (
        <CollectPanModal
          onSuccess={(): void => {
            setShowEditPan(false);
            setView('DETAILS');
          }}
          isOpen={showEditPan}
          onDismiss={handleDismiss}
        />
      ) : null}

      {isSavingDetails ? (
        <EmiPlansLoader
          isLoading={isSavingDetails}
          onDismiss={(): void => {
            setIsSavingDetails(false);
          }}
        />
      ) : null}
    </>
  );
};

export default Details;
